import type { DomainValidationResult } from '../types/security';

/**
 * Validate if a URL is allowed based on domain whitelist
 */
export function validateDomain(
  url: string,
  allowedDomains: string[]
): DomainValidationResult {
  // If no domains specified, allow all
  if (allowedDomains.length === 0) {
    return {
      allowed: true,
      domain: extractDomain(url)
    };
  }

  try {
    const targetUrl = new URL(url, window.location.origin);
    const targetDomain = targetUrl.hostname;

    // Check if domain matches any allowed pattern
    const isAllowed = allowedDomains.some(pattern => {
      return matchDomainPattern(targetDomain, pattern);
    });

    return {
      allowed: isAllowed,
      domain: targetDomain,
      reason: isAllowed ? undefined : `Domain "${targetDomain}" is not in the allowed list`
    };
  } catch (error) {
    return {
      allowed: false,
      domain: url,
      reason: `Invalid URL: ${url}`
    };
  }
}

/**
 * Check if current page domain is allowed
 */
export function validateCurrentDomain(allowedDomains: string[]): DomainValidationResult {
  return validateDomain(window.location.href, allowedDomains);
}

/**
 * Match domain against pattern (supports wildcards)
 */
function matchDomainPattern(domain: string, pattern: string): boolean {
  // Exact match
  if (domain === pattern) {
    return true;
  }

  // Wildcard pattern (e.g., "*.example.com")
  if (pattern.startsWith('*.')) {
    const baseDomain = pattern.substring(2);
    return domain === baseDomain || domain.endsWith('.' + baseDomain);
  }

  // Check if domain ends with pattern (for subdomain matching)
  if (pattern.startsWith('.')) {
    return domain.endsWith(pattern) || domain === pattern.substring(1);
  }

  return false;
}

/**
 * Extract domain from URL
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.hostname;
  } catch {
    return url;
  }
}

/**
 * Check if URL is same-origin
 */
export function isSameOrigin(url: string): boolean {
  try {
    const targetUrl = new URL(url, window.location.origin);
    return targetUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Check if URL uses a safe protocol
 */
export function isSafeProtocol(url: string): boolean {
  try {
    const targetUrl = new URL(url, window.location.origin);
    const protocol = targetUrl.protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Validate navigation target
 */
export function validateNavigationTarget(
  url: string,
  allowedDomains: string[]
): DomainValidationResult {
  // Check protocol first
  if (!isSafeProtocol(url)) {
    return {
      allowed: false,
      domain: url,
      reason: 'Only HTTP and HTTPS protocols are allowed'
    };
  }

  // Check domain
  return validateDomain(url, allowedDomains);
}
